if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.checkboxInput.disabled = false;
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.hiddenControlField.disabled = true;
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        // Past send dates are not allowed: grey them out in the date picker.
        this.sendonInput.min = this.todayInBuyerTimezone();
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.addEventListener('change', this.onChange.bind(this));
        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (this.isEventForThisForm(event)) this.resetRecipientForm();
        });

        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (this.isEventForThisForm(event)) this.displayErrorMessage(event.message, event.errors);
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.cartErrorUnsubscriber) this.cartErrorUnsubscriber();
      }

      // The theme's variant picker publishes no variant-change event, so the current
      // variant is read straight from the product form's hidden `id` input.
      currentProductVariantId() {
        return this.closest('form')?.querySelector('[name="id"]')?.value || this.dataset.productVariantId;
      }

      isEventForThisForm(event) {
        return (
          event.source === 'product-form' && String(event.productVariantId) === String(this.currentProductVariantId())
        );
      }

      onChange() {
        const strings = window.accessibilityStrings || {};
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          this.recipientFieldsLiveRegion.innerText = strings.recipientFormExpanded || '';
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText = strings.recipientFormCollapsed || '';
        }
      }

      todayInBuyerTimezone() {
        const now = new Date();
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
        return now.toISOString().slice(0, 10);
      }

      // The product form is `novalidate`, so a past date typed by hand would still be submitted.
      // Called by product-form before add to cart; returns false and shows the field error if so.
      validateSendOn() {
        if (!this.checkboxInput.checked || !this.sendonInput.value) return true;
        if (this.sendonInput.value >= this.todayInBuyerTimezone()) return true;

        this.displayErrorMessage(null, { send_on: [this.dataset.sendOnPastError] });
        return false;
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      // Each error is shown once, under its own field, and focus moves to the first invalid
      // field (in page order) so it is announced. The summary box is only used for errors
      // with no matching field. String errors are left to product-form's general banner.
      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        if (!body || typeof body !== 'object') return;

        const inputsByKey = {
          email: this.emailInput,
          name: this.nameInput,
          message: this.messageInput,
          send_on: this.sendonInput,
        };
        const unmatchedMessages = [];

        Object.entries(body).forEach(([key, value]) => {
          const message = Array.isArray(value) ? value.join(', ') : String(value);
          const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
          const errorMessageElement = this.querySelector(`#${errorMessageId}`);
          const errorTextElement = errorMessageElement?.querySelector('.error-message');
          if (!errorTextElement) {
            unmatchedMessages.push(message);
            return;
          }

          errorTextElement.innerText = `${message}.`;
          errorMessageElement.classList.remove('hidden');

          const inputElement = inputsByKey[key];
          if (!inputElement) return;

          inputElement.setAttribute('aria-invalid', true);
          inputElement.setAttribute('aria-describedby', errorMessageId);
        });

        if (unmatchedMessages.length) {
          this.errorMessageWrapper.hidden = false;
          this.errorMessage.innerText = this.defaultErrorHeader;
          unmatchedMessages.forEach((message) => {
            const li = document.createElement('li');
            li.className = 'error-message';
            li.innerText = message;
            this.errorMessageList?.appendChild(li);
          });
        }

        this.inputFields()
          .find((inputElement) => inputElement.getAttribute('aria-invalid') === 'true')
          ?.focus();
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        this.inputFields().forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.onChange();
        }
      }
    }
  );
}
